import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { api, openFile } from "@/lib/api";
import { fmtDate, fmtMoney, fmtNumber } from "@/lib/format";
import { useRowDensity } from "@/lib/useRowDensity";
import RowDensityToggle from "@/components/RowDensityToggle";
import StatementBalanceActions, { SourceTag } from "@/components/statements/StatementBalanceActions";
import { useAuth } from "@/lib/auth";
import { ChainBadge, useStatementChain } from "@/components/statements/StatementChain";
import { toast } from "sonner";
import {
  UploadCloud, FileText, FileSearch, CheckCircle2, AlertCircle, Trash2, RefreshCw,
  Settings2, Download, FileWarning, Eye, ChevronLeft, ChevronRight, X, AlertTriangle,
} from "lucide-react";

const STMT_PAGE_SIZE = 25;

const STATUS_BADGE = {
  processing: { label: "Parsing", color: "var(--ll-warning)" },
  parsed: { label: "Parsed", color: "var(--ll-success)" },
  failed: { label: "Failed", color: "var(--ll-error)" },
};

const DELIMITERS = [
  { value: "", label: "Auto-detect" },
  { value: ",", label: "Comma  ( , )" },
  { value: ";", label: "Semicolon  ( ; )" },
  { value: "\t", label: "Tab" },
  { value: "|", label: "Pipe  ( | )" },
];

const humanBytes = (n) => {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export default function Statements() {
  const { cfg: densityCfg } = useRowDensity();
  const { user } = useAuth();
  const canEditBalances = ["owner", "admin", "accountant"].includes(user?.role);
  const [chainKey, setChainKey] = useState(0);
  const chain = useStatementChain(null, chainKey);
  const [accounts, setAccounts] = useState([]);
  const [statements, setStatements] = useState([]);
  const [stmtTotal, setStmtTotal] = useState(0);
  const [preArchiveCount, setPreArchiveCount] = useState(0);
  const [filterPreArchive, setFilterPreArchive] = useState(false);
  const [preArchiveBannerDismissed, setPreArchiveBannerDismissed] = useState(() =>
    localStorage.getItem("ll_stmts_pa_banner_dismissed") === "1",
  );
  const [stmtPage, setStmtPage] = useState(0);
  const [selectedAcc, setSelectedAcc] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);  // 0-100 while POST streams up
  const [uploadResult, setUploadResult] = useState(null);   // {kind: "success"|"error", title, message, fileName}
  const [fileName, setFileName] = useState("");
  const [fileType, setFileType] = useState("");  // pdf | csv | xlsx | ""
  const [showCsvOptions, setShowCsvOptions] = useState(false);
  const [csvDelimiter, setCsvDelimiter] = useState("");
  const [duplicate, setDuplicate] = useState(null);  // {original, lastFormData}
  const [preview, setPreview] = useState(null);  // {data, pending: true}
  const [previewLoading, setPreviewLoading] = useState(false);
  const [dismissedWarnings, setDismissedWarnings] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("ll_dismissed_stmt_warnings") || "[]")); }
    catch { return new Set(); }
  });
  const prevStatusRef = useRef({});
  const fileRef = useRef(null);
  // Statements → Year/Month grouping. Default on; user can toggle flat view.
  const [groupByMonth, setGroupByMonth] = useState(() => {
    const stored = localStorage.getItem("ll_stmts_group_by_month");
    return stored == null ? true : stored === "true";
  });
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  useEffect(() => {
    localStorage.setItem("ll_stmts_group_by_month", String(groupByMonth));
  }, [groupByMonth]);

  const stmtTotalPages = Math.max(1, Math.ceil(stmtTotal / STMT_PAGE_SIZE));
  const stmtFrom = stmtTotal === 0 ? 0 : stmtPage * STMT_PAGE_SIZE + 1;
  const stmtTo = Math.min(stmtTotal, stmtPage * STMT_PAGE_SIZE + statements.length);

  const loadAccounts = async () => {
    const r = await api.get("/bank-accounts");
    setAccounts(r.data);
    if (r.data.length && !selectedAcc) setSelectedAcc(r.data[0].id);
  };
  const loadStatements = async (pageOverride = stmtPage) => {
    const r = await api.get("/statements", {
      params: { paginated: true, limit: STMT_PAGE_SIZE, skip: pageOverride * STMT_PAGE_SIZE },
    });
    setStatements(r.data.items || []);
    setChainKey((k) => k + 1);
    setStmtTotal(r.data.total || 0);
    setPreArchiveCount(r.data.pre_archive_count || 0);
  };
  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([loadAccounts(), loadStatements()]);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  // Reload statements when page changes
  useEffect(() => { loadStatements(stmtPage); /* eslint-disable-next-line */ }, [stmtPage]);

  // Auto-refresh while processing
  useEffect(() => {
    const hasProcessing = statements.some((s) => s.status === "processing");
    if (!hasProcessing) return;
    const t = setInterval(() => loadStatements(stmtPage), 3000);
    return () => clearInterval(t);
  }, [statements, stmtPage]);

  // Fire a sonner toast the moment a statement transitions processing → parsed with a warning_message
  useEffect(() => {
    for (const s of statements) {
      const prev = prevStatusRef.current[s.id];
      if (prev === "processing" && s.status === "parsed" && s.warning_message) {
        toast.warning("Some rows were skipped", {
          description: s.warning_message,
          duration: 8000,
          id: `stmt-warn-${s.id}`,
        });
      }
      prevStatusRef.current[s.id] = s.status;
    }
  }, [statements]);

  const dismissWarning = (id) => {
    const next = new Set(dismissedWarnings);
    next.add(id);
    setDismissedWarnings(next);
    localStorage.setItem("ll_dismissed_stmt_warnings", JSON.stringify([...next]));
  };
  const visibleWarnings = statements.filter(
    (s) => s.status === "parsed" && s.warning_message && !dismissedWarnings.has(s.id),
  );

  const detectType = (name) => {
    const n = name.toLowerCase();
    if (n.endsWith(".pdf")) return "pdf";
    if (n.endsWith(".csv")) return "csv";
    if (n.endsWith(".xlsx") || n.endsWith(".xls")) return "xlsx";
    return "";
  };

  const handleFileChange = () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { setFileName(""); setFileType(""); return; }
    setFileName(f.name);
    const t = detectType(f.name);
    setFileType(t);
    if (t === "csv") setShowCsvOptions(true);
    setDuplicate(null);
  };

  const buildFormData = (force = false) => {
    const file = fileRef.current?.files?.[0];
    if (!file) return null;
    const fd = new FormData();
    fd.append("bank_account_id", selectedAcc);
    fd.append("file", file);
    if (fileType === "csv" && csvDelimiter) fd.append("csv_delimiter", csvDelimiter);
    if (force) fd.append("force", "true");
    return fd;
  };

  const performUpload = async (fd) => {
    setUploading(true);
    setUploadProgress(0);
    const uploadedName = fd.get("file")?.name || "statement";
    try {
      const res = await api.post("/statements/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      setUploadProgress(100);
      const warning = res.data?.warning?.message || null;
      setUploadResult({
        kind: "success",
        title: "Statement uploaded successfully",
        message: warning
          ? `${warning} Parsing is running in the background — refresh the list in a few seconds.`
          : "Your file was received. AI parsing is running in the background — refresh the list in a few seconds.",
        fileName: uploadedName,
      });
      if (fileRef.current) fileRef.current.value = "";
      setFileName(""); setFileType(""); setDuplicate(null);
      loadStatements();
    } catch (err) {
      const data = err?.response?.data;
      const detail = data?.detail;
      if (err?.response?.status === 409 && typeof detail === "object" && detail?.code === "duplicate_statement") {
        setDuplicate({ original: detail.original, message: detail.message });
        setUploadResult({
          kind: "error",
          title: "Duplicate file detected",
          message: detail.message || "This file was already uploaded. Review the duplicate options below.",
          fileName: uploadedName,
        });
      } else {
        setUploadResult({
          kind: "error",
          title: "Statement upload failed",
          message: (typeof detail === "string" ? detail : detail?.message) || "Something went wrong. Please try again.",
          fileName: uploadedName,
        });
      }
    } finally {
      setUploading(false);
    }
  };

  const onUpload = async (e) => {
    e.preventDefault();
    if (!selectedAcc) { toast.error("Select a bank account first"); return; }
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a file to upload"); return; }

    // For CSV/XLSX show a preview-confirm step BEFORE persisting. PDFs skip
    // preview (AI parses async after upload).
    if (fileType === "csv" || fileType === "xlsx") {
      setPreviewLoading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        if (fileType === "csv" && csvDelimiter) fd.append("csv_delimiter", csvDelimiter);
        const res = await api.post("/statements/preview", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPreview(res.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Preview failed");
      } finally { setPreviewLoading(false); }
      return;
    }

    const fd = buildFormData(false);
    if (!fd) { toast.error("Choose a file to upload"); return; }
    await performUpload(fd);
  };

  const confirmUploadFromPreview = async () => {
    setPreview(null);
    const fd = buildFormData(false);
    if (fd) await performUpload(fd);
  };

  const forceUpload = async () => {
    const fd = buildFormData(true);
    if (!fd) return;
    await performUpload(fd);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f && fileRef.current) {
      const dt = new DataTransfer();
      dt.items.add(f);
      fileRef.current.files = dt.files;
      handleFileChange();
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this statement and its transactions?")) return;
    try {
      await api.delete(`/statements/${id}`);
      toast.success("Statement removed");
      loadStatements();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const openOriginal = async (s) => {
    const typeToMime = { pdf: "application/pdf", csv: "text/csv", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" };
    try {
      await openFile(`/statements/${s.id}/download`, typeToMime[s.file_type]);
    } catch (err) {
      // axios responseType:'blob' delivers error bodies as blobs — parse them for the detail.
      let msg = "Original file not available";
      try {
        const blob = err?.response?.data;
        if (blob && typeof blob.text === "function") {
          const text = await blob.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) msg = parsed.detail;
        }
      } catch { /* keep default */ }
      toast.error(msg);
    }
  };

  const accountName = (id) => {
    const a = accounts.find((x) => x.id === id);
    return a ? `${a.name} · ${a.bank_name}` : "—";
  };

  // Mark duplicate-by-hash statements (same file_hash exists more than once)
  const dupHashCounts = (() => {
    const m = new Map();
    for (const s of statements) {
      if (!s.file_hash) continue;
      m.set(s.file_hash, (m.get(s.file_hash) || 0) + 1);
    }
    return m;
  })();

  const isStatementDuplicate = (s) =>
    s.file_hash && (dupHashCounts.get(s.file_hash) || 0) > 1;

  // Statement row renderer. Returns a flat list of <tr> rows when
  // `groupByMonth` is off, or a mix of group-header rows + statement rows
  // when it's on. Group keys are "YYYY-MM" based on period_start (falling
  // back to uploaded_at so a parsed-but-undated statement still shows up).
  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const groupKeyFor = (s) => {
    const d = (s.period_start || s.uploaded_at || "").slice(0, 7);
    return d || "unknown";
  };
  const groupLabel = (key) => {
    if (key === "unknown") return "Unknown period";
    const [y, m] = key.split("-");
    const dt = new Date(Number(y), Number(m) - 1, 1);
    return `${dt.toLocaleString(undefined, { month: "long" })} ${y}`;
  };
  const renderStatementRow = (s) => {
    const badge = STATUS_BADGE[s.status] || STATUS_BADGE.processing;
    const isDup = isStatementDuplicate(s);
    return (
      <tr key={s.id} className="border-b hover:bg-[#E0F2FE]" style={{ borderColor: "var(--ll-border)" }} data-testid={`statement-row-${s.id}`}>
        <td className={`px-5 ${densityCfg.tdY}`}>
          <div className="flex items-center gap-2">
            <FileText size={14} />
            <span className="truncate max-w-[260px]">{s.file_name}</span>
            {!s.storage_path && (
              <span
                data-testid={`statement-prearchive-badge-${s.id}`}
                title="Uploaded before file-archiving was enabled — no side-by-side verify available. Re-upload to enable."
                className="text-[10px] uppercase tracking-widest px-1.5 py-0.5"
                style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #D97706" }}
              >
                No file
              </span>
            )}
            <ChainBadge entry={chain.statements[s.id]} id={s.id} />
            {isDup && (
              <span
                data-testid={`statement-dup-badge-${s.id}`}
                title="Another statement with the same file fingerprint exists"
                className="text-[10px] uppercase tracking-widest px-1.5 py-0.5"
                style={{ background: "#FEF2F2", color: "var(--ll-error)", border: "1px solid var(--ll-error)" }}
              >
                Dup
              </span>
            )}
          </div>
        </td>
        <td className={`px-5 ${densityCfg.tdY} text-[var(--ll-ink-soft)]`}>{accountName(s.bank_account_id)}</td>
        <td className={`px-5 ${densityCfg.tdY} font-mono-fin text-xs`}>{fmtDate(s.period_start)} → {fmtDate(s.period_end)}</td>
        <td className={`px-5 ${densityCfg.tdY} font-mono-fin text-right`}>{fmtNumber(s.transaction_count)}</td>
        <td className={`px-5 ${densityCfg.tdY} font-mono-fin text-right`} data-testid={`statement-closing-${s.id}`}>
          {s.closing_balance != null ? fmtMoney(s.closing_balance) : "—"}
          {s.closing_balance_source === "manual" && <SourceTag source="manual" testid={`statement-closing-source-${s.id}`} />}
        </td>
        <td className={`px-5 ${densityCfg.tdY}`}>
          <span className="text-[10px] uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1" style={{ background: "var(--ll-mint)", color: badge.color }}>
            {s.status === "parsed" && <CheckCircle2 size={11} />}
            {s.status === "failed" && <AlertCircle size={11} />}
            {badge.label}
          </span>
          {s.status === "failed" && s.error_message && <div className="text-[10px] mt-1 text-[var(--ll-error)] max-w-xs truncate" title={s.error_message}>{s.error_message}</div>}
          {s.status === "parsed" && s.warning_message && <div data-testid={`statement-warning-${s.id}`} className="text-[10px] mt-1 text-[var(--ll-ochre)] max-w-xs truncate" title={s.warning_message}>{s.warning_message}</div>}
        </td>
        <td className={`px-5 ${densityCfg.tdY} text-[var(--ll-ink-soft)] text-xs`}>{fmtDate(s.uploaded_at)}</td>
        <td className={`px-5 ${densityCfg.tdY} text-right`}>
          <div className="inline-flex items-center gap-1">
            <Link
              to={(() => {
                const params = new URLSearchParams();
                if (s.bank_account_id) params.set("bank_account_id", s.bank_account_id);
                if (s.period_start) params.set("start", s.period_start);
                if (s.period_end) params.set("end", s.period_end);
                return `/app/transactions?${params.toString()}`;
              })()}
              data-testid={`statement-view-${s.id}`}
              title={s.status === "parsed" ? "View transactions from this statement" : "Statement still parsing — view anyway"}
              className="p-1.5 border inline-flex items-center justify-center hover:bg-[var(--ll-mint)] transition-colors"
              style={{ borderColor: "var(--ll-border)" }}
            >
              <Eye size={14} />
            </Link>
            <button
              onClick={() => openOriginal(s)}
              disabled={!s.storage_path}
              data-testid={`statement-open-${s.id}`}
              title={s.storage_path ? "Open the original file to compare entries" : "Original file not archived — upload was before file-archiving was enabled"}
              className="p-1.5 border inline-flex items-center justify-center hover:bg-[var(--ll-mint)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              style={{ borderColor: "var(--ll-border)" }}
            >
              <FileSearch size={14} />
            </button>
            {canEditBalances && (
              <StatementBalanceActions
                statement={s}
                onUpdated={(u) => {
                  setStatements((arr) => arr.map((x) => (x.id === u.id ? u : x)));
                  setChainKey((k) => k + 1);
                }}
              />
            )}
            <button
              onClick={() => del(s.id)}
              data-testid={`statement-delete-${s.id}`}
              title="Delete statement and its transactions"
              className="p-1.5 border inline-flex items-center justify-center hover:bg-[var(--ll-error)] hover:text-white hover:border-[var(--ll-error)] transition-colors"
              style={{ borderColor: "var(--ll-border)" }}
            >
              <Trash2 size={14} />
            </button>
          </div>
        </td>
      </tr>
    );
  };
  const renderStatementRows = () => {
    const base = filterPreArchive
      ? statements.filter((s) => !s.storage_path)
      : statements;
    if (!groupByMonth) return base.map(renderStatementRow);
    // Build groups preserving the server's sort (uploaded_at desc).
    const groups = [];
    const byKey = new Map();
    for (const s of base) {
      const k = groupKeyFor(s);
      if (!byKey.has(k)) {
        const g = { key: k, items: [], size: 0, txns: 0 };
        byKey.set(k, g);
        groups.push(g);
      }
      const g = byKey.get(k);
      g.items.push(s);
      g.size += s.file_size || 0;
      g.txns += s.transaction_count || 0;
    }
    const out = [];
    for (const g of groups) {
      const collapsed = collapsedGroups.has(g.key);
      out.push(
        <tr
          key={`grp-${g.key}`}
          className="border-b cursor-pointer text-white"
          style={{ borderColor: "var(--ll-border)", background: "#0b5d9e" }}
          onClick={() => toggleGroup(g.key)}
          data-testid={`stmt-group-${g.key}`}
        >
          <td colSpan={8} className="px-5 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ChevronRight
                  size={14}
                  className="transition-transform text-white"
                  style={{ transform: collapsed ? "rotate(0deg)" : "rotate(90deg)" }}
                />
                <span className="font-display font-bold text-sm text-white">{groupLabel(g.key)}</span>
                <span className="text-[10px] uppercase tracking-widest text-white/60">
                  {g.items.length} statement{g.items.length === 1 ? "" : "s"} · {fmtNumber(g.txns)} txns · {humanBytes(g.size)}
                </span>
              </div>
            </div>
          </td>
        </tr>,
      );
      if (!collapsed) {
        for (const s of g.items) out.push(renderStatementRow(s));
      }
    }
    return out;
  };

  return (
    <>
      {/* Upload progress overlay */}
      {uploading && (
        <div
          className="fixed inset-0 z-[90] bg-black/50 grid place-items-center p-4"
          data-testid="upload-progress-overlay"
          role="status"
          aria-live="polite"
        >
          <div className="bg-white w-full max-w-md border shadow-xl p-6" style={{ borderColor: "var(--ll-border)" }}>
            <div className="flex items-center gap-3 mb-3">
              <UploadCloud size={18} className="text-[#0b5d9e]" />
              <div className="font-display font-bold text-sm">Uploading statement…</div>
            </div>
            <div className="h-2 w-full bg-[var(--ll-mint)] overflow-hidden mb-2" data-testid="upload-progress-bar">
              <div
                className="h-full bg-[#0b5d9e] transition-all duration-200"
                style={{ width: `${uploadProgress}%` }}
                data-testid="upload-progress-bar-fill"
              />
            </div>
            <div className="flex items-center justify-between text-xs text-[var(--ll-ink-soft)]">
              <span className="truncate pr-2">{fileName || "statement"}</span>
              <span className="font-mono-fin whitespace-nowrap" data-testid="upload-progress-pct">{uploadProgress}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Result dialog */}
      {uploadResult && (
        <div
          className="fixed inset-0 z-[95] bg-black/50 grid place-items-center p-4"
          onClick={() => setUploadResult(null)}
          data-testid={`upload-result-${uploadResult.kind}`}
          role="dialog"
          aria-modal="true"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-md border shadow-xl"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <div
              className="px-5 py-3 border-b flex items-center gap-3 text-white"
              style={{ background: uploadResult.kind === "success" ? "#0b5d9e" : "#B91C1C", borderColor: "var(--ll-border)" }}
            >
              {uploadResult.kind === "success"
                ? <CheckCircle2 size={18} />
                : <AlertCircle size={18} />}
              <div className="font-display font-bold text-sm">{uploadResult.title}</div>
            </div>
            <div className="px-5 py-4 text-sm text-[var(--ll-ink)]">
              <div className="mb-2 text-xs text-[var(--ll-ink-soft)] font-mono-fin truncate" data-testid="upload-result-filename">
                {uploadResult.fileName}
              </div>
              <p className="leading-relaxed" data-testid="upload-result-message">{uploadResult.message}</p>
            </div>
            <div className="px-5 py-3 border-t flex items-center justify-end gap-2 bg-[var(--ll-bg-card)]" style={{ borderColor: "var(--ll-border)" }}>
              <button
                type="button"
                onClick={() => setUploadResult(null)}
                data-testid="upload-result-close"
                className="text-xs uppercase tracking-widest px-3 py-1.5 bg-[#0b5d9e] text-white hover:opacity-90"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <PageHeader
        title="Statements"
        subtitle="Bank statement uploads"
        testid="statements-header"
        actions={
          <div className="flex items-center gap-2">
            <RowDensityToggle testid="statements-density" />
            <button onClick={loadStatements} className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5" style={{ borderColor: "var(--ll-border)" }} data-testid="refresh-statements-btn">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        }
      />
      <PageBody>
        {loading && <div className="ll-progress mb-6" />}

        {visibleWarnings.length > 0 && (
          <div
            className="mb-4 border-l-4 pl-4 pr-3 py-3 flex items-start gap-3"
            style={{ background: "#FFF7E6", borderColor: "var(--ll-ochre)" }}
            data-testid="history-cap-banner"
          >
            <AlertTriangle size={18} className="mt-0.5 shrink-0" style={{ color: "var(--ll-ochre)" }} />
            <div className="flex-1 space-y-2">
              <div className="text-sm font-medium text-[var(--ll-ink)]">
                {visibleWarnings.length === 1
                  ? "1 recent upload had rows outside the 60-month window"
                  : `${visibleWarnings.length} recent uploads had rows outside the 60-month window`}
              </div>
              <ul className="space-y-1">
                {visibleWarnings.map((s) => (
                  <li
                    key={s.id}
                    className="text-xs text-[var(--ll-ink-soft)] flex items-start justify-between gap-3"
                    data-testid={`history-cap-banner-item-${s.id}`}
                  >
                    <span className="flex-1">
                      <span className="font-medium text-[var(--ll-ink)]">{s.file_name}</span>
                      {" — "}
                      {s.warning_message}
                    </span>
                    <button
                      onClick={() => dismissWarning(s.id)}
                      data-testid={`history-cap-dismiss-${s.id}`}
                      className="text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)] shrink-0"
                      aria-label="Dismiss"
                    >
                      <X size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <form
          onSubmit={onUpload}
          className="ll-card-flat p-6 mb-4"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          data-testid="upload-form"
        >
          <div className="grid lg:grid-cols-12 gap-4 items-end">
            <div className="lg:col-span-4">
              <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Bank account</label>
              <select
                value={selectedAcc} onChange={(e) => setSelectedAcc(e.target.value)}
                data-testid="upload-account-select"
                className="w-full px-3 py-2 border bg-white" style={{ borderColor: "var(--ll-border)" }}
              >
                {accounts.length === 0 && <option value="">No accounts — add one first</option>}
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.bank_name}</option>)}
              </select>
            </div>
            <div className="lg:col-span-6">
              <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">File (PDF, CSV, or Excel)</label>
              <label className="block border border-dashed py-4 px-3 text-sm cursor-pointer hover:bg-[var(--ll-mint)] transition-colors" style={{ borderColor: "var(--ll-ink)" }} data-testid="upload-dropzone">
                <input
                  ref={fileRef} type="file" accept=".pdf,.csv,.xlsx,.xls" className="hidden"
                  onChange={handleFileChange}
                  data-testid="upload-file-input"
                />
                <div className="flex items-center gap-3">
                  <UploadCloud size={18} />
                  <span className="truncate">
                    {fileName ? fileName : "Click to choose or drag & drop a statement file (max 20MB)"}
                  </span>
                  {fileType && (
                    <span className="ml-auto text-[10px] uppercase tracking-widest px-2 py-1" style={{ background: "var(--ll-mint)" }}>
                      {fileType}
                    </span>
                  )}
                </div>
              </label>
            </div>
            <div className="lg:col-span-2">
              <button
                type="submit" disabled={uploading || previewLoading || !selectedAcc}
                data-testid="upload-submit-btn"
                className="w-full px-4 py-2.5 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] transition-colors disabled:opacity-60"
              >
                {uploading ? "Uploading…" : previewLoading ? "Previewing…" : "Upload"}
              </button>
            </div>
          </div>

          {/* CSV options */}
          {fileType === "csv" && (
            <div className="mt-4 border-t pt-4" style={{ borderColor: "var(--ll-border)" }} data-testid="csv-options-panel">
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setShowCsvOptions((v) => !v)}
                  className="flex items-center gap-2 text-sm text-[var(--ll-ink)] hover:underline"
                  data-testid="csv-options-toggle"
                >
                  <Settings2 size={14} />
                  {showCsvOptions ? "Hide CSV options" : "Show CSV options"}
                </button>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/statements/sample.csv`}
                  download
                  data-testid="csv-sample-download"
                  className="text-sm flex items-center gap-1.5 text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)]"
                >
                  <Download size={14} /> Download sample CSV
                </a>
              </div>
              {showCsvOptions && (
                <div className="grid md:grid-cols-2 gap-4 mt-3 text-sm">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Delimiter</label>
                    <select
                      value={csvDelimiter}
                      onChange={(e) => setCsvDelimiter(e.target.value)}
                      data-testid="csv-delimiter-select"
                      className="w-full px-3 py-2 border bg-white"
                      style={{ borderColor: "var(--ll-border)" }}
                    >
                      {DELIMITERS.map((d) => (
                        <option key={d.label} value={d.value}>{d.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="text-xs text-[var(--ll-ink-soft)] leading-relaxed">
                    <div className="font-medium text-[var(--ll-ink)] mb-1">Expected columns</div>
                    <span className="font-mono-fin">Date · Description · Amount</span> (signed; negatives = debits) and an optional <span className="font-mono-fin">Balance</span>. We also accept separate <span className="font-mono-fin">Debit</span> / <span className="font-mono-fin">Credit</span> columns. Header names like &ldquo;Posted Date&rdquo;, &ldquo;Narration&rdquo;, or &ldquo;Memo&rdquo; are auto-mapped.
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="text-xs text-[var(--ll-ink-soft)] mt-3">
            Up to 60 months of statements per account. Parsing typically completes within ~30 seconds. Duplicates are automatically detected by file fingerprint.
          </div>
        </form>

        {/* Duplicate banner */}
        {duplicate && (
          <div className="mb-6 border p-4 flex flex-wrap items-start gap-3" style={{ borderColor: "var(--ll-error)", background: "#FEF2F2" }} data-testid="duplicate-banner">
            <FileWarning size={18} className="text-[var(--ll-error)] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0 text-sm">
              <div className="font-medium text-[var(--ll-error)]">Duplicate file detected</div>
              <div className="text-[var(--ll-ink-soft)] mt-1">
                This exact file (by fingerprint) was already uploaded as{" "}
                <span className="font-medium text-[var(--ll-ink)]">{duplicate.original.file_name}</span>{" "}
                on <span className="font-mono-fin">{fmtDate(duplicate.original.uploaded_at)}</span> ({duplicate.original.transaction_count} transactions, status{" "}
                <span className="font-medium">{duplicate.original.status}</span>).
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setDuplicate(null)}
                data-testid="duplicate-dismiss-btn"
                className="text-sm px-3 py-2 border hover:bg-white"
                style={{ borderColor: "var(--ll-border)" }}
              >
                Cancel
              </button>
              <button
                onClick={forceUpload}
                disabled={uploading}
                data-testid="duplicate-force-btn"
                className="text-sm px-4 py-2 bg-[var(--ll-error)] text-white hover:opacity-90 disabled:opacity-60"
              >
                {uploading ? "Re-uploading…" : "Upload anyway"}
              </button>
            </div>
          </div>
        )}

        {/* Preview modal — column detection + sample before commit */}
        {preview && (
          <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="preview-modal">
            <div className="bg-white max-w-4xl w-full max-h-[90vh] overflow-auto border shadow-xl" style={{ borderColor: "var(--ll-border)" }}>
              <div className="px-5 py-4 border-b flex items-start justify-between gap-3" style={{ borderColor: "var(--ll-border)" }}>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Statement preview</div>
                  <div className="font-display font-bold text-xl mt-1">Confirm column detection</div>
                  <div className="text-xs text-[var(--ll-ink-soft)] mt-1 font-mono-fin truncate">
                    {preview.filename} · {(preview.total_rows ?? 0).toLocaleString()} row(s)
                  </div>
                </div>
                <button onClick={() => setPreview(null)} className="p-1.5 hover:bg-[var(--ll-bg-sidebar)]" data-testid="preview-close-btn">
                  <X size={16} />
                </button>
              </div>

              {/* Warnings */}
              {preview.warnings?.length > 0 && (
                <div className="mx-5 mt-4 border p-3 text-sm" style={{ borderColor: "var(--ll-error)", background: "#FEF2F2" }} data-testid="preview-warnings">
                  <div className="font-medium text-[var(--ll-error)] mb-1 flex items-center gap-1.5"><AlertCircle size={14} /> Issues detected</div>
                  <ul className="list-disc ml-5 text-[var(--ll-ink-soft)] space-y-0.5">
                    {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {/* Detected mapping */}
              <div className="px-5 py-4">
                <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2">Detected columns</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs" data-testid="preview-detected-grid">
                  {[
                    ["Date", preview.detected?.date],
                    ["Description", preview.detected?.description],
                    ["Amount", preview.detected?.amount],
                    ["Debit", preview.detected?.debit],
                    ["Credit", preview.detected?.credit],
                    ["Balance", preview.detected?.balance],
                  ].map(([label, val]) => (
                    <div key={label} className="border px-3 py-2" style={{ borderColor: "var(--ll-border)" }} data-testid={`preview-detected-${label.toLowerCase()}`}>
                      <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)]">{label}</div>
                      <div className={`font-mono-fin text-sm ${val ? "text-[var(--ll-ink)]" : "text-[var(--ll-ink-muted)] italic"}`}>
                        {val || "Not found"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sample rows */}
              <div className="px-5 pb-4">
                <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2">First {preview.sample_rows?.length ?? 0} rows</div>
                <div className="border overflow-auto max-h-[40vh]" style={{ borderColor: "var(--ll-border)" }}>
                  <table className="w-full text-xs" data-testid="preview-sample-table">
                    <thead>
                      <tr className="bg-[var(--ll-bg-sidebar)] border-b" style={{ borderColor: "var(--ll-border)" }}>
                        {(preview.columns || []).map((c) => (
                          <th key={c} className="px-2 py-1.5 text-left font-medium text-[10px] uppercase tracking-widest">{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.sample_rows || []).map((row, idx) => (
                        <tr key={idx} className="border-b" style={{ borderColor: "var(--ll-border)" }}>
                          {(preview.columns || []).map((c) => (
                            <td key={c} className="px-2 py-1.5 font-mono-fin whitespace-nowrap">{row[c] ?? ""}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="px-5 py-4 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--ll-border)" }}>
                <button onClick={() => setPreview(null)} data-testid="preview-cancel-btn" className="text-sm px-4 py-2 border hover:bg-[var(--ll-bg-sidebar)]" style={{ borderColor: "var(--ll-border)" }}>
                  Cancel
                </button>
                <button
                  onClick={confirmUploadFromPreview}
                  disabled={uploading || (preview.warnings?.length > 0)}
                  data-testid="preview-confirm-btn"
                  className="text-sm px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  <CheckCircle2 size={14} /> Confirm &amp; parse
                </button>
              </div>
            </div>
          </div>
        )}

        {preArchiveCount > 0 && !preArchiveBannerDismissed && (
          <div
            className="ll-card-flat p-4 mb-5 flex items-start gap-3"
            style={{ background: "#FEF3C7", borderLeft: "4px solid #D97706" }}
            data-testid="pre-archive-banner"
          >
            <AlertCircle size={18} className="shrink-0 mt-0.5" style={{ color: "#D97706" }} />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm">
                {preArchiveCount} statement{preArchiveCount === 1 ? "" : "s"} uploaded before file-archiving
              </div>
              <p className="text-xs text-[var(--ll-ink-soft)] mt-1 max-w-2xl">
                These uploads don&apos;t have the original PDF/CSV stored, so they can&apos;t be used
                for side-by-side verify. To enable it, delete and re-upload the same file —
                new uploads are archived automatically.
              </p>
              <div className="flex items-center gap-2 mt-2.5">
                <button
                  onClick={() => setFilterPreArchive((v) => !v)}
                  data-testid="pre-archive-filter-toggle"
                  className="text-[11px] px-3 py-1 border bg-white hover:bg-[var(--ll-mint)]"
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  {filterPreArchive ? "Show all statements" : "Show only these"}
                </button>
                <button
                  onClick={() => {
                    localStorage.setItem("ll_stmts_pa_banner_dismissed", "1");
                    setPreArchiveBannerDismissed(true);
                  }}
                  data-testid="pre-archive-banner-dismiss"
                  className="text-[11px] px-3 py-1 text-[var(--ll-ink-soft)] hover:underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="ll-card-flat overflow-hidden">
          <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
            <div className="font-display font-bold text-lg">Uploaded statements</div>
            <div className="flex items-center gap-3 text-xs text-[var(--ll-ink-soft)]">
              <span>{statements.length} on this page</span>
              <label className="flex items-center gap-1 cursor-pointer" data-testid="statements-group-toggle">
                <input
                  type="checkbox"
                  checked={groupByMonth}
                  onChange={(e) => setGroupByMonth(e.target.checked)}
                  className="accent-[var(--ll-ink)]"
                />
                Group by month
              </label>
            </div>
          </div>
          {statements.length === 0 ? (
            <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="statements-empty">No statements uploaded yet.</div>
          ) : (
            <table className={`w-full ${densityCfg.txt}`} data-testid="statements-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-white bg-[var(--ll-bg-sidebar)] border-b" style={{ borderColor: "var(--ll-border)" }}>
                  <th className={`px-5 ${densityCfg.tdY} font-medium`}>File</th>
                  <th className={`px-5 ${densityCfg.tdY} font-medium`}>Account</th>
                  <th className={`px-5 ${densityCfg.tdY} font-medium`}>Period</th>
                  <th className={`px-5 ${densityCfg.tdY} font-medium text-right`}>Txns</th>
                  <th className={`px-5 ${densityCfg.tdY} font-medium text-right`}>Closing Balance</th>
                  <th className={`px-5 ${densityCfg.tdY} font-medium`}>Status</th>
                  <th className={`px-5 ${densityCfg.tdY} font-medium`}>Uploaded</th>
                  <th className={`px-5 ${densityCfg.tdY}`}></th>
                </tr>
              </thead>
              <tbody>
                <tr aria-hidden="true" data-testid="statements-header-spacer">
                  <td colSpan={8} className="h-5 bg-white" style={{ borderBottom: "1px solid var(--ll-border)" }}></td>
                </tr>
                {renderStatementRows()}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination footer */}
        {stmtTotal > 0 && (
          <div
            className="ll-card-flat mt-3 px-4 py-3 flex flex-wrap items-center justify-between gap-3"
            data-testid="statements-pagination"
          >
            <div className="text-xs text-[var(--ll-ink-soft)] font-mono-fin uppercase tracking-widest">
              Showing <span className="text-[var(--ll-ink)]">{stmtFrom}–{stmtTo}</span> of <span className="text-[var(--ll-ink)]">{stmtTotal.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setStmtPage((p) => Math.max(0, p - 1))}
                disabled={stmtPage === 0}
                data-testid="stmt-page-prev"
                className="text-sm px-3 py-1.5 border hover:bg-[var(--ll-mint)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                style={{ borderColor: "var(--ll-border)" }}
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <span className="text-xs px-3 font-mono-fin" data-testid="stmt-page-indicator">
                Page {stmtPage + 1} / {stmtTotalPages}
              </span>
              <button
                onClick={() => setStmtPage((p) => Math.min(stmtTotalPages - 1, p + 1))}
                disabled={stmtPage >= stmtTotalPages - 1}
                data-testid="stmt-page-next"
                className="text-sm px-3 py-1.5 border hover:bg-[var(--ll-mint)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                style={{ borderColor: "var(--ll-border)" }}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}
